import React from "react";
import { Eye, EyeOff } from "lucide-react";
import PasswordPolicyMeter from "./PasswordPolicyMeter";
import { isPasswordValid } from "../lib/passwordPolicy";

/**
 * Iter269.12 — Drop-in password input + confirm + meter + suggester.
 *
 * The whole "set a password" UX in one component. Every signup, reset,
 * invite-accept, or change-password screen can drop this in and get the
 * full feature set: live policy checklist, strength bar, suggester
 * button that pre-fills both inputs, mismatch warning, submit-gating
 * via `onValid`.
 *
 * Parent contract:
 *   ```jsx
 *   const [pw, setPw] = useState(null);
 *   <PasswordWithMeter onValid={setPw} />
 *   <button disabled={!pw} onClick={() => submit(pw)} />
 *   ```
 * - `onValid(pw)` is called with the password string when the meter
 *   confirms ALL policy rules pass AND (if applicable) the confirm
 *   field matches. Called with `null` whenever validity drops.
 * - Optional `onChange(pw)` fires on every keystroke if a parent needs
 *   to observe typing (analytics, password-manager autofill races).
 *
 * Use `requireConfirm={false}` for single-field screens (FreeScan signup).
 * Use `requireConfirm={true}` (default) for reset / invite / change-password.
 *
 * Iter269.13 — `blockOnBreach` (default true) flips `onValid` to null
 *   when HIBP says the password has appeared in any known breach. Fail-
 *   open on HIBP network errors so a transient outage doesn't lock the
 *   user out — the visible warning is reserved for confirmed breaches.
 */
export default function PasswordWithMeter({
  onValid,
  onChange,
  requireConfirm = true,
  newPasswordLabel = "NEW PASSWORD",
  confirmLabel = "CONFIRM",
  newPasswordPlaceholder = "9+ chars · upper/lower/number/special",
  confirmPlaceholder = "Re-type the new password",
  autoComplete = "new-password",
  autoFocus = false,
  testIdPrefix = "pw",
  showSuggester = true,
  showStrengthBar = true,
  breachCheckEnabled = true,
  blockOnBreach = true,
  className = "",
}) {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [meterValid, setMeterValid] = React.useState(false);
  const [breachInfo, setBreachInfo] = React.useState(null);

  // Compute combined validity (rules + match + not-breached) and notify
  // upstream when it flips. Fail-open on HIBP errors — the visible
  // banner is reserved for confirmed breach hits only.
  const lastEmittedRef = React.useRef(null);
  React.useEffect(() => {
    const matches = requireConfirm ? pw === pw2 : true;
    const breached = blockOnBreach && breachInfo?.pwned === true;
    const ok = meterValid && matches && pw.length > 0 && !breached;
    const next = ok ? pw : null;
    if (next !== lastEmittedRef.current) {
      lastEmittedRef.current = next;
      onValid?.(next);
    }
  }, [pw, pw2, meterValid, breachInfo, requireConfirm, blockOnBreach, onValid]);

  const handleChange = (next) => {
    setPw(next);
    onChange?.(next);
  };

  // Suggester pre-fills both inputs (when confirm is required) so the
  // user lands in a "submit-ready" state in one click.
  const handleSuggest = (suggested) => {
    setPw(suggested);
    if (requireConfirm) setPw2(suggested);
    onChange?.(suggested);
  };

  // Mismatch warning only renders once the user has typed *something*
  // in the confirm field — we don't shame them for an empty confirm
  // while they're still typing the primary.
  const showMismatch = requireConfirm && pw2 && pw !== pw2;

  // Iter273.2 — Beta-tester feedback: account setup forms were missing
  // a show/hide password toggle. Independent state per field so a user
  // can reveal their typed primary while still hiding the confirm.
  const [show1, setShow1] = React.useState(false);
  const [show2, setShow2] = React.useState(false);

  return (
    <div className={className} data-testid={`${testIdPrefix}-wrap`}>
      <div>
        <div className="qt-label mb-2" id={`${testIdPrefix}-pw-label`}>
          {newPasswordLabel}
        </div>
        <div style={{ position: "relative" }}>
          <input
            type={show1 ? "text" : "password"}
            className="qt-input"
            required
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            placeholder={newPasswordPlaceholder}
            aria-labelledby={`${testIdPrefix}-pw-label`}
            data-testid={`${testIdPrefix}-input`}
            value={pw}
            minLength={9}
            onChange={(e) => handleChange(e.target.value)}
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setShow1((v) => !v)}
            aria-label={show1 ? "Hide password" : "Show password"}
            data-testid={`${testIdPrefix}-eye`}
            tabIndex={-1}
            style={{
              position: "absolute", right: 8, top: "50%",
              transform: "translateY(-50%)",
              background: "transparent", border: "none",
              color: "var(--qt-text-3, #A5B5CE)",
              padding: 6, cursor: "pointer", borderRadius: 6,
              display: "inline-flex", alignItems: "center",
            }}
          >
            {show1 ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
          </button>
        </div>
        <PasswordPolicyMeter
          value={pw}
          onValidityChange={setMeterValid}
          onBreachChange={setBreachInfo}
          onSuggest={showSuggester ? handleSuggest : undefined}
          showStrengthBar={showStrengthBar}
          breachCheckEnabled={breachCheckEnabled}
          testIdPrefix={`${testIdPrefix}-policy`}
        />
      </div>

      {requireConfirm && (
        <div className="mt-3">
          <div className="qt-label mb-2" id={`${testIdPrefix}-confirm-label`}>
            {confirmLabel}
          </div>
          <div style={{ position: "relative" }}>
            <input
              type={show2 ? "text" : "password"}
              className="qt-input"
              required
              autoComplete={autoComplete}
              placeholder={confirmPlaceholder}
              aria-labelledby={`${testIdPrefix}-confirm-label`}
              data-testid={`${testIdPrefix}-confirm`}
              value={pw2}
              minLength={9}
              onChange={(e) => setPw2(e.target.value)}
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShow2((v) => !v)}
              aria-label={show2 ? "Hide password" : "Show password"}
              data-testid={`${testIdPrefix}-confirm-eye`}
              tabIndex={-1}
              style={{
                position: "absolute", right: 8, top: "50%",
                transform: "translateY(-50%)",
                background: "transparent", border: "none",
                color: "var(--qt-text-3, #A5B5CE)",
                padding: 6, cursor: "pointer", borderRadius: 6,
                display: "inline-flex", alignItems: "center",
              }}
            >
              {show2 ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
          </div>
          {showMismatch && (
            <div
              className="qt-mono text-[11px] mt-2"
              data-testid={`${testIdPrefix}-mismatch`}
              style={{ color: "#FF6E6E" }}
            >
              Passwords don't match.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Re-export the predicate so single-line consumers can also check
// validity inline without importing two paths.
export { isPasswordValid };
